import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Flag, Loader2, Edit2, Save, X, AlertCircle, CheckCircle } from 'lucide-react';
import { reachCloseOutAPI } from '../../services/reachService';

type CloseOutStatus = 'draft' | 'final';

interface CloseOut {
  status: CloseOutStatus;
  generatedAt?: string;
  content?: string;
  editedContent?: string;
  dataGates?: {
    minPulseWeeks: number;
    minTrackerWeeks: number;
    met: boolean;
  };
}

const STATUS_CHIP: Record<CloseOutStatus, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  final: { label: 'Final', cls: 'bg-green-50 text-green-700 border-green-200' },
};

export const CloseOutPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [closeout, setCloseout] = useState<CloseOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [markingFinal, setMarkingFinal] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    const r = await reachCloseOutAPI.get(projectId);
    setCloseout(r.data.closeout ?? null);
  }, [projectId]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const saveEdit = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await reachCloseOutAPI.update(projectId, { editedContent: editDraft });
      setCloseout(prev => prev ? { ...prev, editedContent: editDraft } : prev);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const markFinal = async () => {
    if (!projectId) return;
    setMarkingFinal(true);
    try {
      await reachCloseOutAPI.update(projectId, { status: 'final' });
      setCloseout(prev => prev ? { ...prev, status: 'final' } : prev);
    } finally {
      setMarkingFinal(false);
    }
  };

  if (!projectId) return null;

  const display = closeout?.editedContent ?? closeout?.content;
  const status = closeout?.status;
  const gates = closeout?.dataGates;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Flag size={22} />
          </div>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Close-Out Report</h1>
              <p className="text-slate-500 text-sm font-medium">Summarise programme outcomes and lessons learned</p>
            </div>
            {status && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_CHIP[status].cls}`}>
                {STATUS_CHIP[status].label}
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {display && !editing && status !== 'final' && (
              <>
                <button
                  onClick={() => { setEditDraft(display); setEditing(true); }}
                  className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  <Edit2 size={14} /> Edit
                </button>
                <button
                  onClick={markFinal}
                  disabled={markingFinal}
                  className="flex items-center gap-2 rounded-xl h-9 px-4 border border-green-200 bg-green-50 text-green-700 text-sm font-bold hover:bg-green-100 transition-colors disabled:opacity-60"
                >
                  <CheckCircle size={14} /> {markingFinal ? 'Saving…' : 'Mark as final'}
                </button>
              </>
            )}
            {editing && (
              <>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
                >
                  <Save size={14} /> {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="flex items-center gap-1.5 rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-500 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  <X size={14} /> Cancel
                </button>
              </>
            )}
          </div>
        </header>

        {/* Data gates status */}
        {gates && (
          <div className="flex items-center gap-4 flex-wrap">
            <div className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border ${
              gates.met ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-500'
            }`}>
              {gates.met ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
              Min. {gates.minPulseWeeks} pulse weeks required
            </div>
            <div className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg border ${
              gates.met ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-500'
            }`}>
              {gates.met ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
              Min. {gates.minTrackerWeeks} tracker weeks required
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        ) : (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
            {!display && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Flag size={28} className="text-slate-400" />
                </div>
                <p className="text-slate-500 text-sm">No close-out report yet.</p>
                <p className="text-slate-400 text-xs mt-1">Drafted by the change management skill in Claude Code (/reach-sponsor-brief). Requires at least half the engagement's weeks of pulse and tracker data.</p>
              </div>
            )}

            {display && (
              <div className="p-6">
                {editing ? (
                  <textarea
                    rows={20}
                    value={editDraft}
                    onChange={e => setEditDraft(e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-4 py-3 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                  />
                ) : (
                  <>
                    {closeout?.editedContent && (
                      <div className="flex items-center gap-1.5 mb-4">
                        <Edit2 size={12} className="text-primary" />
                        <span className="text-xs text-primary font-semibold">Edited version</span>
                        <button
                          onClick={() => {
                            reachCloseOutAPI.update(projectId, { editedContent: null });
                            setCloseout(prev => prev ? { ...prev, editedContent: undefined } : prev);
                          }}
                          className="ml-2 text-xs text-slate-400 hover:text-red-400 transition-colors"
                        >
                          Revert to generated
                        </button>
                      </div>
                    )}
                    <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed whitespace-pre-line">
                      {display}
                    </div>
                    {closeout?.generatedAt && (
                      <p className="mt-4 text-xs text-slate-400">
                        Generated {new Date(closeout.generatedAt).toLocaleString()}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CloseOutPage;
